(() => {
  class LuvatyMotion {
    constructor() {
      this.preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 760px)');
      this.items = new Map();
      this.animations = new Map();
      this.carousels = new WeakSet();
      this.observer = null;
      this.supported = 'IntersectionObserver' in window && typeof Element.prototype.animate === 'function';
      this.scan();
      this.refresh();
      this.preference.addEventListener('change', () => this.refresh());
      document.addEventListener('shopify:section:load', () => { this.scan(); this.refresh(); });
      document.addEventListener('shopify:section:unload', () => this.prune());
      document.addEventListener('focusin', (event) => this.revealFocused(event.target));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.cancelAll();
      });
      window.addEventListener('pagehide', () => this.cancelAll());
      window.addEventListener('pageshow', (event) => { if (event.persisted) this.refresh(); });
      document.documentElement.classList.add('motion-ready');
    }

    register(selector, kind) {
      document.querySelectorAll(selector).forEach((selected) => {
        const element = kind === 'image' ? selected.parentElement : selected;
        if (this.items.has(element) || element.closest('[hidden], .mobile-nav, .product-form, .product-details, .wellness-faq__items')) return;
        this.items.set(element, { kind, visible: false, seen: false });
        element.dataset.motion = kind;
      });
    }

    scan() {
      this.prune();
      this.register('#MainContent h1, #MainContent h2', 'heading');
      this.register('.collection-product-card, .ritual-component, .stage-tile__content, .quality-standard__points > article, .stage-hero__needs > a', 'card');
      this.register('.stage-hero__portrait img, .stage-tile__art img, .quality-standard__media img, .ritual-feature__visual img, .skin-ritual-feature__portrait img, .product-gallery__item img', 'image');
      this.register([
        '.luvaty-hero__intro > .eyebrow', '.luvaty-hero__copy', '.luvaty-hero__actions',
        '.stage-hero__copy > .eyebrow', '.stage-hero__lead', '.stage-hero__actions',
        '.stage-shop__heading .eyebrow', '.stage-shop__lead', '.wellness-section-head > p',
        '.quality-standard__content > .eyebrow', '.quality-standard__lead', '.quality-standard__promise',
        '.founder-letter__intro > p', '.founder-letter__letter > p', '.founder-letter__signoff',
        '.ritual-feature__copy > p', '.ritual-feature__copy > ul', '.ritual-feature__purchase',
        '.skin-ritual-feature__copy > p', '.skin-ritual-feature__copy > ul',
        '.product-info__lead', '.newsletter-ritual__inner > .eyebrow', '.brand-footer__top > p'
      ].join(','), 'copy');
      document.querySelectorAll('.wellness-product-grid, .wellness-pack-grid, .ritual-component-grid, .stage-hero__needs, .quality-standard__points').forEach((group) => {
        [...group.children].forEach((item, index) => {
          const state = this.items.get(item);
          if (state) state.delay = (index % 4) * 65;
        });
      });
      this.bindMobileCarousels();
    }

    splitHeading(heading) {
      if (heading.classList.contains('motion-heading') || heading.querySelector('a, button, input')) return;
      const label = heading.textContent.replace(/\s+/g, ' ').trim();
      if (!label || label.split(' ').length > 30) return;
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) fragment.appendChild(document.createTextNode(part));
          else {
            const word = document.createElement('span');
            word.className = 'motion-word';
            word.setAttribute('aria-hidden', 'true');
            word.textContent = part;
            fragment.appendChild(word);
          }
        });
        node.replaceWith(fragment);
      });
      heading.classList.add('motion-heading');
      if (!heading.hasAttribute('aria-label')) heading.setAttribute('aria-label', label);
    }

    refresh() {
      this.observer?.disconnect();
      this.cancelAll();
      const enabled = this.supported && !this.preference.matches;
      document.documentElement.classList.toggle('motion-enabled', enabled);
      document.documentElement.classList.toggle('motion-disabled', !enabled);
      if (!enabled) return;
      this.items.forEach((state, element) => {
        state.visible = false;
        state.seen = false;
        if (state.kind === 'heading') this.splitHeading(element);
      });
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const state = this.items.get(entry.target);
          if (!state || entry.isIntersecting === state.visible) return;
          state.visible = entry.isIntersecting;
          entry.target.dataset.motionState = state.visible ? 'entered' : 'exited';
          if (state.visible) {
            state.seen = true;
            this.enter(entry.target, state);
          } else if (state.seen) {
            this.leave(entry.target, state);
          }
        });
      }, { rootMargin: '-64px 0px -4% 0px', threshold: 0 });
      this.items.forEach((_, element) => this.observer.observe(element));
    }

    animate(owner, target, frames, options) {
      const animation = target.animate(frames, {
        duration: this.mobile.matches ? 620 : 850,
        easing: 'cubic-bezier(.16,1,.3,1)',
        fill: 'backwards',
        ...options
      });
      animation.id = 'luvaty-' + owner.dataset.motion;
      const active = this.animations.get(owner) || new Set();
      active.add(animation);
      this.animations.set(owner, active);
      const release = () => {
        active.delete(animation);
        if (!active.size && this.animations.get(owner) === active) this.animations.delete(owner);
      };
      animation.onfinish = release;
      animation.oncancel = release;
    }

    enter(element, state) {
      this.cancel(element);
      if (element.contains(document.activeElement) || this.preference.matches) return;
      const compact = this.mobile.matches;
      if (state.kind === 'heading' && element.querySelector('.motion-word')) {
        const hero = Boolean(element.closest('.luvaty-hero, .stage-hero'));
        element.querySelectorAll('.motion-word').forEach((word, index) => {
          this.animate(element, word, [
            { opacity: hero ? .65 : .12, translate: `0 ${compact ? '.3em' : '.55em'}`, rotate: compact ? '0deg' : 'x 12deg' },
            { opacity: 1, translate: '0 0', rotate: '0deg' }
          ], { duration: compact ? 680 : 1050, delay: Math.min(index * (compact ? 25 : 38), 280) });
        });
      } else if (state.kind === 'image') {
        element.querySelectorAll('img').forEach((image) => {
          this.animate(element, image, [{ scale: compact ? '1.025' : '1.055' }, { scale: '1' }], { duration: compact ? 950 : 1400 });
        });
      } else if (state.kind === 'card') {
        [...element.children].forEach((child) => {
          const interactive = child.matches('a, button, input') || child.querySelector('a, button, input');
          this.animate(element, child, [
            { opacity: .25, translate: interactive ? '0 0' : `0 ${compact ? '12px' : '24px'}` },
            { opacity: 1, translate: '0 0' }
          ], { delay: compact ? Math.min(state.delay || 0, 90) : state.delay || 0 });
        });
      } else {
        this.animate(element, element, [{ opacity: .25 }, { opacity: 1 }], { delay: 80 });
      }
    }

    leave(element, state) {
      this.cancel(element);
      if (element.contains(document.activeElement) || state.kind === 'image' || this.preference.matches) return;
      const targets = state.kind === 'heading' ? element.querySelectorAll('.motion-word') : state.kind === 'card' ? element.children : [element];
      [...targets].forEach((target) => {
        this.animate(element, target, [{ opacity: 1 }, { opacity: .65 }], { duration: 240, easing: 'ease-out', fill: 'none' });
      });
    }

    cancel(element) {
      this.animations.get(element)?.forEach((animation) => animation.cancel());
      this.animations.delete(element);
    }

    cancelAll() {
      [...this.animations.keys()].forEach((element) => this.cancel(element));
    }

    revealFocused(target) {
      this.items.forEach((_, element) => {
        if (element.contains(target)) this.cancel(element);
      });
    }

    prune() {
      this.items.forEach((_, element) => {
        if (element.isConnected) return;
        this.cancel(element);
        this.observer?.unobserve(element);
        this.items.delete(element);
      });
    }

    bindMobileCarousels() {
      document.querySelectorAll('.ritual-preview-grid, .featured-rituals__grid').forEach((carousel) => {
        if (this.carousels.has(carousel)) return;
        this.carousels.add(carousel);
        let ticking = false;
        const update = () => {
          const bounds = carousel.getBoundingClientRect();
          const center = bounds.left + bounds.width / 2;
          let closest = null;
          let distance = Infinity;
          [...carousel.children].forEach((card) => {
            const rect = card.getBoundingClientRect();
            const next = Math.abs(rect.left + rect.width / 2 - center);
            if (next < distance) { distance = next; closest = card; }
          });
          [...carousel.children].forEach((card) => card.classList.toggle('is-motion-current', card === closest));
          ticking = false;
        };
        carousel.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        }, { passive: true });
        update();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => new LuvatyMotion(), { once: true });
  else new LuvatyMotion();
})();
